import React, { useMemo, useState, useCallback } from 'react';
import ReactFlow, {
  Node,
  Edge,
  Controls,
  Background,
  MarkerType,
  Handle,
  Position,
  ConnectionMode,
  BackgroundVariant
} from 'reactflow';
import 'reactflow/dist/style.css';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import { updateReviewEntity } from '../../services/api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface HierarchyEntity {
  id: number;
  name: string;
  description?: string;
  org_entity_code?: string;
  review_entity_type_id: number;
  review_entity_type?: {
    id: number;
    name: string;
    display_label: string;
    category?: string;
    description?: string;
  };
  owner_display_name?: string;
  owner_name?: string;
  status?: string;
  parents?: { id: number; name: string; review_entity_type_id: number }[];
  parent_ids?: number[];
}

export interface EntityHierarchyGraphProps {
  entityId: number;
  allEntities: HierarchyEntity[];
  onClose: () => void;
  /**
   * Called after the focused entity's parents are successfully updated.
   * Parent component should refetch the entity list so the graph re-renders.
   * May return a Promise; if it does, the save flow awaits it before closing
   * the edit dialog so the refreshed data is in state by the time the graph
   * is shown again.
   */
  onSaved?: () => void | Promise<void>;
}

// ---------------------------------------------------------------------------
// Custom Node
// ---------------------------------------------------------------------------

const NODE_WIDTH = 220;
const NODE_HEIGHT = 68;

interface CustomNodeData {
  label: string;
  entityType: string;
  entityId: number;
  role: 'focused' | 'parent' | 'child';
  status?: string;
  ownerName?: string;
  orgCode?: string;
}

const HierarchyNode: React.FC<{ data: CustomNodeData }> = ({ data }) => {
  const bgColor =
    data.role === 'focused'
      ? '#1565c0'
      : data.role === 'parent'
      ? '#e3f2fd'
      : '#e8f5e9';

  const borderColor =
    data.role === 'focused'
      ? '#0d47a1'
      : data.role === 'parent'
      ? '#90caf9'
      : '#a5d6a7';

  const textColor = data.role === 'focused' ? '#fff' : '#212121';
  const subtextColor = data.role === 'focused' ? 'rgba(255,255,255,0.8)' : '#757575';

  return (
    <div
      style={{
        width: NODE_WIDTH,
        minHeight: NODE_HEIGHT,
        borderRadius: 8,
        border: `2px solid ${borderColor}`,
        background: bgColor,
        padding: '8px 12px',
        textAlign: 'center',
        boxShadow:
          data.role === 'focused'
            ? '0 4px 12px rgba(21,101,192,0.35)'
            : '0 1px 4px rgba(0,0,0,0.1)',
        cursor: 'default',
        position: 'relative',
      }}
    >
      <Handle
        type="target"
        position={Position.Top}
        style={{ background: borderColor, width: 8, height: 8, top: -5 }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        style={{ background: borderColor, width: 8, height: 8, bottom: -5 }}
      />

      <div
        style={{
          fontWeight: 600,
          fontSize: 13,
          color: textColor,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
        title={data.label}
      >
        {data.label}
      </div>
      <div
        style={{
          fontSize: 11,
          color: subtextColor,
          marginTop: 2,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
        title={data.entityType}
      >
        {data.entityType}
      </div>
      {data.orgCode && (
        <div
          style={{
            fontSize: 10,
            color: subtextColor,
            marginTop: 1,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
          title={data.orgCode}
        >
          {data.orgCode}
        </div>
      )}
    </div>
  );
};

const nodeTypes = { hierarchyNode: HierarchyNode };

// ---------------------------------------------------------------------------
// Layout helpers – manual BFS-based DAG layout
// ---------------------------------------------------------------------------

/**
 * Build adjacency maps from the entity list.
 * parentMap : entityId -> set of parent ids
 * childMap  : entityId -> set of child ids
 */
function buildAdjacency(entities: HierarchyEntity[]) {
  const parentMap = new Map<number, Set<number>>();
  const childMap = new Map<number, Set<number>>();
  const entityMap = new Map<number, HierarchyEntity>();

  entities.forEach((e) => {
    entityMap.set(e.id, e);
    if (!parentMap.has(e.id)) parentMap.set(e.id, new Set());
    if (!childMap.has(e.id)) childMap.set(e.id, new Set());
  });

  entities.forEach((e) => {
    const pIds = getParentIds(e);
    pIds.forEach((pid) => {
      parentMap.get(e.id)?.add(pid);
      if (!childMap.has(pid)) childMap.set(pid, new Set());
      childMap.get(pid)?.add(e.id);
    });
  });

  return { parentMap, childMap, entityMap };
}

/**
 * Collect all descendant entity ids of `focusId` using the child adjacency map.
 * Used to prevent cycles when editing parents.
 */
function collectDescendants(
  focusId: number,
  childMap: Map<number, Set<number>>
): Set<number> {
  const out = new Set<number>();
  const stack: number[] = [focusId];
  while (stack.length) {
    const id = stack.pop()!;
    const children = childMap.get(id);
    if (!children) continue;
    children.forEach((cid) => {
      if (!out.has(cid)) {
        out.add(cid);
        stack.push(cid);
      }
    });
  }
  return out;
}

function getParentIds(entity: HierarchyEntity): number[] {
  if (entity.parents && entity.parents.length > 0) {
    return entity.parents.map((p) => p.id);
  }
  if (entity.parent_ids && entity.parent_ids.length > 0) {
    return entity.parent_ids;
  }
  return [];
}

/**
 * Collect related entity IDs via BFS in both directions (parents & children),
 * up to a configurable depth.
 */
function collectRelated(
  focusId: number,
  parentMap: Map<number, Set<number>>,
  childMap: Map<number, Set<number>>,
  maxDepth = 4
) {
  // depth: focusId = 0, parents = -1 … , children = +1 …
  const depthMap = new Map<number, number>();
  depthMap.set(focusId, 0);

  // BFS upward (parents)
  let frontier = [focusId];
  for (let d = 1; d <= maxDepth; d++) {
    const next: number[] = [];
    frontier.forEach((id) => {
      const pids = parentMap.get(id);
      if (!pids) return;
      Array.from(pids).forEach((pid) => {
        if (!depthMap.has(pid)) {
          depthMap.set(pid, -d);
          next.push(pid);
        }
      });
    });
    if (next.length === 0) break;
    frontier = next;
  }

  // BFS downward (children)
  frontier = [focusId];
  for (let d = 1; d <= maxDepth; d++) {
    const next: number[] = [];
    frontier.forEach((id) => {
      const cids = childMap.get(id);
      if (!cids) return;
      Array.from(cids).forEach((cid) => {
        if (!depthMap.has(cid)) {
          depthMap.set(cid, d);
          next.push(cid);
        }
      });
    });
    if (next.length === 0) break;
    frontier = next;
  }

  return depthMap;
}

/**
 * Assign x/y positions.  Entities are grouped by depth level and spread
 * horizontally.  The focused node is centred.
 */
function layoutNodes(
  depthMap: Map<number, number>,
  entityMap: Map<number, HierarchyEntity>,
  focusId: number
): { nodes: Node<CustomNodeData>[]; edges: Edge[] } {
  const HORIZONTAL_GAP = NODE_WIDTH + 40;
  const VERTICAL_GAP = 120;

  // Group IDs by depth
  const levels = new Map<number, number[]>();
  Array.from(depthMap.entries()).forEach(([id, depth]) => {
    if (!levels.has(depth)) levels.set(depth, []);
    levels.get(depth)!.push(id);
  });

  // Sort levels: most negative first (top-most parents)
  const sortedDepths = Array.from(levels.keys()).sort((a, b) => a - b);

  // Assign positions
  const posMap = new Map<number, { x: number; y: number }>();
  for (const depth of sortedDepths) {
    const ids = levels.get(depth)!;
    // Sort entities within the same level for deterministic output
    ids.sort((a, b) => {
      const ea = entityMap.get(a);
      const eb = entityMap.get(b);
      return (ea?.name || '').localeCompare(eb?.name || '');
    });

    const totalWidth = ids.length * HORIZONTAL_GAP;
    const startX = -totalWidth / 2 + HORIZONTAL_GAP / 2;
    const y = depth * VERTICAL_GAP;

    ids.forEach((id, idx) => {
      posMap.set(id, { x: startX + idx * HORIZONTAL_GAP, y });
    });
  }

  // Build React Flow nodes
  const nodes: Node<CustomNodeData>[] = [];
  Array.from(posMap.entries()).forEach(([id, pos]) => {
    const entity = entityMap.get(id);
    const depth = depthMap.get(id) ?? 0;
    const role: 'focused' | 'parent' | 'child' =
      id === focusId ? 'focused' : depth < 0 ? 'parent' : 'child';

    nodes.push({
      id: `n-${id}`,
      type: 'hierarchyNode',
      position: pos,
      data: {
        label: entity?.name ?? `Entity ${id}`,
        entityType:
          entity?.review_entity_type?.display_label ??
          entity?.review_entity_type?.name ??
          'Unknown',
        entityId: id,
        role,
        status: entity?.status,
        ownerName: entity?.owner_display_name ?? entity?.owner_name,
        orgCode: entity?.org_entity_code,
      },
    });
  });

  // Build React Flow edges (parent → child direction, i.e. top → bottom)
  const edgeSet = new Set<string>();
  const edges: Edge[] = [];

  Array.from(depthMap.keys()).forEach((id) => {
    const entity = entityMap.get(id);
    if (!entity) return;
    const pids = getParentIds(entity);
    pids.forEach((pid) => {
      if (!depthMap.has(pid)) return; // parent not in visible graph
      const key = `${pid}->${id}`;
      if (edgeSet.has(key)) return;
      edgeSet.add(key);
      edges.push({
        id: `e-${pid}-${id}`,
        source: `n-${pid}`,
        target: `n-${id}`,
        type: 'smoothstep',
        animated: false,
        style: { stroke: '#90caf9', strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#1976d2', width: 16, height: 16 },
      });
    });
  });

  return { nodes, edges };
}

// ---------------------------------------------------------------------------
// Legend
// ---------------------------------------------------------------------------

const Legend: React.FC = () => (
  <Paper
    elevation={2}
    sx={{
      position: 'absolute',
      bottom: 16,
      right: 16,
      zIndex: 10,
      p: 1.5,
      display: 'flex',
      gap: 1.5,
      alignItems: 'center',
      bgcolor: 'rgba(255,255,255,0.95)',
    }}
  >
    <Chip size="small" label="Current Entity" sx={{ bgcolor: '#1565c0', color: '#fff', fontWeight: 600 }} />
    <Chip size="small" label="Parent" variant="outlined" sx={{ borderColor: '#90caf9', bgcolor: '#e3f2fd' }} />
    <Chip size="small" label="Child" variant="outlined" sx={{ borderColor: '#a5d6a7', bgcolor: '#e8f5e9' }} />
  </Paper>
);

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

const EntityHierarchyGraph: React.FC<EntityHierarchyGraphProps> = ({
  entityId,
  allEntities,
  onClose,
  onSaved,
}) => {
  const adjacency = useMemo(() => {
    if (!allEntities || allEntities.length === 0) {
      return {
        parentMap: new Map<number, Set<number>>(),
        childMap: new Map<number, Set<number>>(),
        entityMap: new Map<number, HierarchyEntity>(),
      };
    }
    return buildAdjacency(allEntities);
  }, [allEntities]);

  const { nodes, edges } = useMemo(() => {
    if (!allEntities || allEntities.length === 0) return { nodes: [], edges: [] };
    const depthMap = collectRelated(entityId, adjacency.parentMap, adjacency.childMap);
    return layoutNodes(depthMap, adjacency.entityMap, entityId);
  }, [entityId, allEntities, adjacency]);

  // React Flow keeps its internal state for nodes/edges even when the props
  // change; remount it whenever the underlying graph shape changes so parent
  // edits are reflected immediately.
  const graphKey = useMemo(
    () => `${nodes.length}::${edges.map((e) => e.id).sort().join('|')}`,
    [nodes, edges]
  );

  const focusedEntity = useMemo(
    () => allEntities.find((e) => e.id === entityId),
    [entityId, allEntities]
  );

  // ----- Edit parents dialog state -----
  const [editOpen, setEditOpen] = useState(false);
  const [selectedParents, setSelectedParents] = useState<HierarchyEntity[]>([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const currentParentEntities = useMemo(() => {
    if (!focusedEntity) return [] as HierarchyEntity[];
    const ids = getParentIds(focusedEntity);
    return ids
      .map((id) => allEntities.find((e) => e.id === id))
      .filter((e): e is HierarchyEntity => Boolean(e));
  }, [focusedEntity, allEntities]);

  const parentOptions = useMemo(() => {
    if (!focusedEntity) return [] as HierarchyEntity[];
    const descendants = collectDescendants(focusedEntity.id, adjacency.childMap);
    return allEntities
      .filter((e) => e.id !== focusedEntity.id && !descendants.has(e.id))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [focusedEntity, allEntities, adjacency.childMap]);

  const handleOpenEdit = useCallback(() => {
    setSelectedParents(currentParentEntities);
    setSaveError(null);
    setEditOpen(true);
  }, [currentParentEntities]);

  const handleCloseEdit = useCallback(() => {
    if (saving) return;
    setEditOpen(false);
    setSaveError(null);
  }, [saving]);

  const handleSaveParents = useCallback(async () => {
    if (!focusedEntity) return;
    setSaving(true);
    setSaveError(null);
    try {
      await updateReviewEntity({
        id: focusedEntity.id,
        parent_ids: selectedParents.map((p) => p.id),
      });
      // Wait for the parent to refresh its entity list (if it returns a Promise)
      // so the new parent edges are in state before we close the dialog.
      const maybePromise = onSaved?.();
      if (maybePromise && typeof (maybePromise as Promise<void>).then === 'function') {
        await maybePromise;
      }
      setEditOpen(false);
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? // axios error shape: err.response.data.detail
            ((err as { response?: { data?: { detail?: string } } }).response?.data?.detail ?? null)
          : null;
      setSaveError(message || 'Failed to update parents');
    } finally {
      setSaving(false);
    }
  }, [focusedEntity, selectedParents, onSaved]);

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        zIndex: 1300,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Box
        sx={{
          backgroundColor: 'white',
          borderRadius: 2,
          width: '92%',
          height: '90%',
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 3,
            py: 1.5,
            borderBottom: '1px solid #e0e0e0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            bgcolor: '#fafafa',
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={600}>
              Entity Hierarchy
            </Typography>
            {focusedEntity && (
              <Typography variant="body2" color="text.secondary">
                {focusedEntity.name}
                {focusedEntity.review_entity_type?.display_label &&
                  ` — ${focusedEntity.review_entity_type.display_label}`}
              </Typography>
            )}
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            {focusedEntity && (
              <Tooltip title="Edit parents">
                <span>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={handleOpenEdit}
                  >
                    Edit parents
                  </Button>
                </span>
              </Tooltip>
            )}
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
        </Box>

        {/* Graph area */}
        <Box sx={{ flexGrow: 1, position: 'relative', width: '100%', height: '100%' }}>
          {nodes.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <Typography color="text.secondary">
                No hierarchy data available for this entity.
              </Typography>
            </Box>
          ) : (
            <ReactFlow
              key={graphKey}
              nodes={nodes}
              edges={edges}
              nodeTypes={nodeTypes}
              fitView
              fitViewOptions={{ padding: 0.35 }}
              defaultViewport={{ x: 0, y: 0, zoom: 0.85 }}
              connectionMode={ConnectionMode.Loose}
              nodesConnectable={false}
              nodesDraggable={true}
              elementsSelectable={true}
              zoomOnScroll={true}
              panOnScroll={true}
              minZoom={0.3}
              maxZoom={1.8}
            >
              <Controls showInteractive={false} />
              <Background variant={BackgroundVariant.Dots} gap={20} size={1} color="#e0e0e0" />
              <Legend />
            </ReactFlow>
          )}
        </Box>
      </Box>

      {focusedEntity && (
        <Dialog open={editOpen} onClose={handleCloseEdit} maxWidth="sm" fullWidth>
          <DialogTitle>
            Edit parents
            <Typography variant="body2" color="text.secondary">
              {focusedEntity.name}
              {focusedEntity.review_entity_type?.display_label &&
                ` — ${focusedEntity.review_entity_type.display_label}`}
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Pick the parents for this entity. The entity itself and its descendants are
              excluded to prevent cycles.
            </Typography>
            <Autocomplete<HierarchyEntity, true>
              multiple
              options={parentOptions}
              value={selectedParents}
              getOptionLabel={(option) =>
                option.review_entity_type?.display_label
                  ? `${option.name} — ${option.review_entity_type.display_label}`
                  : option.name
              }
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(_event, newValue) => setSelectedParents(newValue)}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.id}
                    label={option.name}
                    size="small"
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Parents"
                  placeholder={selectedParents.length === 0 ? 'Select parent entities…' : ''}
                />
              )}
              disabled={saving}
            />
            {saveError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {saveError}
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseEdit} disabled={saving}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveParents}
              variant="contained"
              disabled={saving}
              startIcon={saving ? <CircularProgress size={16} /> : null}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default EntityHierarchyGraph;
